import React from "react";
import { connect } from "react-redux";
import { navigate } from "gatsby";
import { keyframes } from "@emotion/core";
import { RiLoader4Line } from "react-icons/ri";
import { IoIosClose } from "react-icons/io";

import Helmet from "../components/Helmet";
import * as Input from "../components/Input";
import isBrowser from "../utils/isBrowser";
import { Permission } from "../utils/permissions";

const ActionButton = ({ name, success, message, processing, disabled, handleClick }) => (
    <div css={{
        fontSize: ".8em",
    }}>
        {
            typeof success !== "undefined" && !processing
            ?   <div css={{ marginBottom: 15 }}>
                    {
                        success === true
                        ?   <div css={{ color: "var(--green)" }}>{ message }</div>
                        :   success === false
                            ?   <div css={{ color: "var(--red)" }}>{ message }</div>
                            :   null
                    }
                </div>
            :   null
        }

        <button
            disabled={ processing || disabled }
            css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "inherit",
                ":disabled": {
                    color: "var(--light)",
                    backgroundColor: "rgba(100, 100, 100, .2)",
                    cursor: "not-allowed",
                },
            }}
            onClick={ handleClick }
        >
            {
                processing
                ?   <RiLoader4Line
                        css={{
                            animation: `${keyframes({
                                from: {
                                    transform: "rotate(0deg)",
                                },
                                to: {
                                    transform: "rotate(360deg)",
                                },
                            })} .8s linear infinite`,
                        }}
                    />
                :   name
            }
        </button>
    </div>
);

const TeamView = ({ code, me, org }) => {
    const [ processing, setProcessing ] = React.useState(false);

    const [ addMemberSuccess, setAddMemberSuccess ] = React.useState(undefined);
    const [ addMemberMessage, setAddMemberMessage ] = React.useState(undefined);

    const [ members, setMembers ] = React.useState([]);
    const [ memberEmail, setMemberEmail ] = React.useState("");

    const addTeamMember = () => {
        if (processing) return;

        // check validity of email
        if (!/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/i.test(memberEmail)) {
            setAddMemberSuccess(false);
            setAddMemberMessage("Please check the email address.");
            return;
        }

        if (!isBrowser()) return;

        setProcessing(true);

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/members", {
            method: "PUT",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
            body: JSON.stringify({
                email: memberEmail,
            }),
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(member => {
            setMembers([
                ...members,
                member,
            ]);
            setAddMemberSuccess(true);
            setAddMemberMessage("Successfully added " + member.user.name + " to the team.");
            setProcessing(false);
        }).catch((e) => {
            console.error(e);
            setAddMemberSuccess(false);
            setAddMemberMessage("Unable to add " + memberEmail + " to the team. Are you sure a Glee user exists with that email?");
            setProcessing(false);
        });
    };

    const deleteTeamMember = (memberId) => {
        if (processing) return;

        if (!isBrowser()) return;

        setProcessing(true);

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/members/" + memberId, {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(member => {
            setMembers(members.filter(m => m._id !== member._id));
            setProcessing(false);
        }).catch((e) => {
            console.error(e);
            setProcessing(false);
        });
    };

    React.useEffect(() => {
        if (org.permissions === Permission.OWNER || org.permissions === Permission.ADMIN) {
            window.fetch(process.env.API_URL + "/orgs/" + org._id + "/members", {
                headers: {
                    Accept: "application/json",
                    Authorization: me.token,
                },
            }).then(res => {
                if (res.ok) return res.json();
                throw new Error(res.statusText);
            }).then(members => {
                console.log(members)
                setMembers(members);
            }).catch((e) => {
                console.error(e);
            });
        } else {
            navigate("/" + org.code);
        }
    }, [ org, me.token ]);

    return (
        <div css={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: 720,
            margin: "0 auto",
        }}>
            <Helmet
                title={ org.name + " - Settings" }
            />
            <div css={{ fontSize: "1.2em", margin: "25px 0" }}>Team</div>


            <div css={{ color: "lightgrey", fontSize: ".9em", marginBottom: 5 }}>Add Members</div>
            <div css={{ color: "darkgrey", fontSize: ".8em", marginBottom: 15 }}>Add Glee users to to your organization. Members can manage feedback in your organization but won't have access to organization settings.</div>
            <div css={{ width: "100%" }}>
                {
                    (org.employee || (org.plan && org.plan.amount >= 500))
                    ?   <div>
                            <Input.Text
                                title="Email"
                                description="If the email belongs to a Glee user, we'll add them to the team."
                                placeholder="e.g. alice@example.com"
                                value={ memberEmail }
                                setValue={ setMemberEmail }
                            />

                            <ActionButton
                                name={ "Add Member" }
                                success={ addMemberSuccess }
                                message={ addMemberMessage }
                                processing={ processing }
                                disabled={ !memberEmail }
                                handleClick={ addTeamMember }
                            />
                        </div>
                    :   <div css={{ color: "var(--orange)", fontSize: ".8em" }}>Upgrade your subscription to add users to your team.</div>
                }
            </div>

            <div css={{ color: "lightgrey", fontSize: ".9em", marginBottom: 5, marginTop: 50 }}>Members</div>
            <div css={{ color: "darkgrey", fontSize: ".8em" }}>
                There { members.length === 1 ? "is" : "are" } { members.length } user{ members.length === 1 ? "" : "s" } in the team.
            </div>
            <div css={{ marginTop: 15 }}>
                {
                    members && members.length
                    ?   members.map(member =>
                            <div css={{
                                marginBottom: 10,
                                padding: 10,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor: "rgba(100, 100, 100, .2)",
                                borderRadius: 3,
                            }}>
                                <div css={{
                                    fontSize: ".9em",
                                }}>
                                    { member.user.name }
                                </div>
                                <div css={{
                                    display: "flex",
                                }}>
                                    <IoIosClose
                                        title="Remove Member"
                                        size={ 20 }
                                        css={{
                                            color: "var(--red)",
                                            borderRadius: "50%",
                                            transition: "background-color var(--transition-default-time) ease",
                                            cursor: "pointer",
                                            ":hover": {
                                                backgroundColor: "rgba(255, 0, 0, .3)",
                                            },
                                        }}
                                        onClick={ () => deleteTeamMember(member._id) }
                                    />
                                </div>
                            </div>
                        )
                    :   null
                }
            </div>
        </div>
    );
};

export default connect(
    state => ({ me: state.global.me, org: state.global.org }),
    null,
)(TeamView);
